import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Tabs,
  Tab,
  Fab,
  Alert,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalculateIcon from '@mui/icons-material/Calculate';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import FarmCalculator from './FarmCalculator';
import FarmCard from './FarmCard';
import ManageWorkersDialog from './ManageWorkersDialog';
import FarmSettingsDialog from './FarmSettingsDialog';
import { v4 as uuidv4 } from 'uuid';
import type { Worker } from '../../types/worker';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { logSupabaseOperation } from '../../utils/supabaseLogger';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface Farm {
  id: string;
  user_id?: string;
  name: string;
  location: string;
  size: number;
  status?: string;
  description?: string;
  crops?: string[];
  created_at?: string;
  updated_at?: string;
  imageUrl?: string;
}

const MyFarms = () => {
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [openWorkersDialog, setOpenWorkersDialog] = useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Omit<Farm, 'id'>>({
    name: '',
    location: '',
    size: 0,
    status: 'active',
    description: '',
    crops: [],
  });

  // Tutorial mode state
  const [tutorialMode, setTutorialMode] = useState(() => {
    const stored = localStorage.getItem('tutorialMode');
    return stored ? JSON.parse(stored) : true;
  });

  // NEW: Add a visible toggle/button to switch modes
  const handleToggleTutorialMode = () => {
    setTutorialMode((prev: boolean) => {
      const next = !prev;
      localStorage.setItem('tutorialMode', JSON.stringify(next));
      return next;
    });
  };

  // Mock data with UUIDs for tutorial (no workers/stats fields)
  const tutorialFarms: Farm[] = [
    {
      id: uuidv4(),
      name: 'Green Valley Farm',
      location: 'Nakuru County',
      imageUrl: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1489&q=80',
      size: 10,
      status: 'active',
      description: '',
      crops: [],
    },
    {
      id: uuidv4(),
      name: 'Sunrise Dairy Farm',
      location: 'Kiambu County',
      imageUrl: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1489&q=80',
      size: 15,
      status: 'active',
      description: '',
      crops: [],
    },
  ];

  // Farms state: use tutorial data if tutorialMode, else real data
  const [farms, setFarms] = useState<Farm[]>(tutorialMode ? tutorialFarms : []);
  const [farmStats, setFarmStats] = useState<Record<string, { workersCount: number; tasksCount: number; workers: Worker[]; tasks: any[] }>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // NEW: Helper to fetch workers and tasks for a farm and calculate stats
  const fetchFarmStats = async (farmId: string) => {
    const [{ data: workers = [] }, { data: tasks = [] }] = await Promise.all([
      supabase.from('workers').select('*').eq('farm_id', farmId),
      supabase.from('tasks').select('*').eq('farm_id', farmId),
    ]);
    return {
      workersCount: (workers || []).length,
      tasksCount: (tasks || []).length,
      workers: workers || [],
      tasks: tasks || [],
    };
  };

  // Fetch farms from Supabase
  const fetchFarms = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('farms')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (error) {
      setError('Failed to fetch farms.');
      setFarms([]);
      setFarmStats({});
    } else {
      // Map image_url to imageUrl for frontend
      const mapped = (data || []).map(farm => ({
        ...farm,
        imageUrl: farm.image_url,
      }));
      setFarms(mapped);
      // Fetch stats for each farm
      const statsEntries = await Promise.all(
        (mapped || []).map(async (farm) => [farm.id, await fetchFarmStats(farm.id)])
      );
      setFarmStats(Object.fromEntries(statsEntries));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (tutorialMode) {
      setFarms(tutorialFarms);
    } else {
      fetchFarms();
    }
    localStorage.setItem('tutorialMode', JSON.stringify(tutorialMode));
    // eslint-disable-next-line
  }, [tutorialMode, user]);

  // Function to end tutorial and switch to real data
  const endTutorial = () => setTutorialMode(false);
  // Function to start tutorial (from settings)
  const startTutorial = () => setTutorialMode(true);

  const handleAddFarm = () => {
    setSelectedFarm(null);
    setFormData({
      name: '',
      location: '',
      size: 0,
      status: 'active',
      description: '',
      crops: [],
    });
    setOpenDialog(true);
  };

  const handleEditFarm = (farmId: string) => {
    const farm = farms.find(f => f.id === farmId);
    if (farm) {
      setSelectedFarm(farm);
      setFormData(farm);
      setOpenDialog(true);
    }
  };

  const handleManageWorkers = (farmId: string) => {
    const farm = farms.find(f => f.id === farmId);
    if (farm) {
      setSelectedFarm(farm);
      setOpenWorkersDialog(true);
    }
  };

  const handleSettings = (farmId: string) => {
    const farm = farms.find(f => f.id === farmId);
    if (farm) {
      setSelectedFarm(farm);
      setOpenSettingsDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFarm(null);
  };

  // Helper to insert audit log
  const insertAuditLog = async (action: string, table: string, recordId: string, oldData: any, newData: any, userId: string) => {
    const { error } = await supabase.from('audit_logs').insert([
      {
        table_name: table,
        record_id: recordId,
        action,
        old_data: oldData ? JSON.stringify(oldData) : null,
        new_data: newData ? JSON.stringify(newData) : null,
        edited_by: userId,
      },
    ]);
    if (error) {
      // eslint-disable-next-line no-console
      console.error('[AuditLog] Error inserting audit log:', error);
    }
  };

  // Prevent adding farms to Supabase when in tutorial mode
  const handleSaveFarm = async (farmData: Omit<Farm, 'id'>) => {
    let oldFarm = null;
    if (selectedFarm) {
      // Fetch old data for audit log
      const { data: oldData } = await supabase.from('farms').select('*').eq('id', selectedFarm.id).single();
      oldFarm = oldData;
    }
    const { data, error } = selectedFarm
      ? await supabase.from('farms').update(farmData).eq('id', selectedFarm.id).select()
      : user ? await supabase.from('farms').insert([{ ...farmData, user_id: user.id }]).select() : { data: null, error: { message: 'No user' } };
    logSupabaseOperation(selectedFarm ? 'Update Farm' : 'Insert Farm', data, error);
    if (!error && data && data[0] && user) {
      await insertAuditLog(
        selectedFarm ? 'update' : 'insert',
        'farms',
        data[0].id,
        oldFarm,
        data[0],
        user.id
      );
      setOpenDialog(false);
      fetchFarms();
    }
  };

  const handleUpdateSettings = async (settings: Partial<Farm>) => {
    if (!user) return; // Guard for user possibly being null
    if (tutorialMode) {
    if (selectedFarm) {
      setFarms(farms.map(farm =>
        farm.id === selectedFarm.id ? { ...farm, ...settings } : farm
      ));
    }
    setOpenSettingsDialog(false);
      return;
    }
    if (!selectedFarm) return;
    setLoading(true);
    setError(null);
    try {
      // Always include notifications, privacy, storage as JSONB (or empty objects)
      const updateData = {
        name: settings.name,
        description: settings.description,
        location: settings.location,
        image_url: settings.imageUrl,
        notifications: (settings as any).notifications ?? {},
        privacy: (settings as any).privacy ?? {},
        storage: (settings as any).storage ?? {},
      };
      const { error } = await supabase
        .from('farms')
        .update(updateData)
        .eq('id', selectedFarm.id)
        .eq('user_id', user.id);
      if (error) throw error;
      fetchFarms();
    } catch (err: any) {
      setError('Failed to update farm settings.');
    }
    setOpenSettingsDialog(false);
    setLoading(false);
  };

  const handleUploadImage = async (file: File): Promise<string> => {
    try {
      // Fallback to local URL if no user (e.g. tutorial mode)
      if (!user) {
        return URL.createObjectURL(file);
      }
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Try to upload to 'farms' bucket
      const { error: uploadError } = await supabase.storage
        .from('farms')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('farms')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading image:', error);
      // Fallback to local preview if upload fails
      return URL.createObjectURL(file);
    }
  };

  const calculateWages = (workers: Worker[]) => {
    const now = new Date();
    const startOfDay = new Date(now.setHours(0, 0, 0, 0));
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return workers.reduce((totals, worker) => {
      const attendance = worker.attendance || [];
      const dailyAttendance = attendance.filter(date => new Date(date) >= startOfDay).length;
      const weeklyAttendance = attendance.filter(date => new Date(date) >= startOfWeek).length;
      const monthlyAttendance = attendance.filter(date => new Date(date) >= startOfMonth).length;

      let dailyWage = 0;
      let weeklyWage = 0;
      let monthlyWage = 0;

      switch (worker.paymentType) {
        case 'daily':
          dailyWage = worker.wage * dailyAttendance;
          weeklyWage = worker.wage * weeklyAttendance;
          monthlyWage = worker.wage * monthlyAttendance;
          break;
        case 'weekly':
          dailyWage = (worker.wage / 7) * dailyAttendance;
          weeklyWage = worker.wage * Math.ceil(weeklyAttendance / 7);
          monthlyWage = worker.wage * 4;
          break;
        case 'monthly':
          dailyWage = (worker.wage / 30) * dailyAttendance;
          weeklyWage = (worker.wage / 4) * Math.ceil(weeklyAttendance / 7);
          monthlyWage = worker.wage;
          break;
        default:
          dailyWage = worker.wage;
          weeklyWage = worker.wage * 7;
          monthlyWage = worker.wage * 30;
      }

      return {
        daily: totals.daily + dailyWage,
        weekly: totals.weekly + weeklyWage,
        monthly: totals.monthly + monthlyWage,
      };
    }, { daily: 0, weekly: 0, monthly: 0 });
  };

  // NEW: Delete farm
  const handleDeleteFarm = async (farmId: string) => {
    // Fetch old data for audit log
    const { data: oldData } = await supabase.from('farms').select('*').eq('id', farmId).single();
    const { data, error } = await supabase.from('farms').delete().eq('id', farmId).select();
    logSupabaseOperation('Delete Farm', data, error);
    if (!error && user) {
      await insertAuditLog('delete', 'farms', farmId, oldData, null, user.id);
      fetchFarms();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">My Farms</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={tutorialMode ? 'contained' : 'outlined'}
            color={tutorialMode ? 'info' : 'primary'}
            onClick={handleToggleTutorialMode}
            disabled={loading}
          >
            {tutorialMode ? 'Tutorial Mode' : 'Real Data Mode'}
          </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddFarm}
            disabled={loading}
        >
          Add Farm
        </Button>
        </Box>
      </Box>

      {/* Tutorial mode indicator - show a Chip and Alert if in tutorial mode */}
      {tutorialMode && (
        <Box sx={{ mb: 2 }}>
          <Chip label="Tutorial Mode" color="info" sx={{ mr: 2 }} />
          <Alert severity="info" sx={{ mt: 2 }}>
            You are viewing mock data as a tutorial. Add your own farms or switch to real data mode to use real data.
          </Alert>
        </Box>
      )}

      {/* Empty state if no farms and not in tutorial mode */}
      {!tutorialMode && farms.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have no farms yet. Click <strong>Add Farm</strong> to get started!
        </Alert>
      )}

      <Grid container spacing={3}>
        {farms.map((farm) => (
          <Grid item xs={12} sm={6} md={4} key={farm.id}>
            {/* Pass color and stats to FarmCard for visual distinction and data display */}
            <FarmCard
              farm={farm}
              onEdit={handleEditFarm}
              onManageWorkers={handleManageWorkers}
              onSettings={handleSettings}
              color={tutorialMode ? 'info.main' : 'primary.main'}
              stats={{
                tasks: farmStats[farm.id]?.tasksCount || 0,
                workers: farmStats[farm.id]?.workersCount || 0,
                acres: farm.size,
              }}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedFarm ? 'Edit Farm' : 'Add New Farm'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Farm Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Location"
            fullWidth
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={() => handleSaveFarm(formData)} variant="contained" disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Farm Settings Dialog */}
      <FarmSettingsDialog
        open={openSettingsDialog}
        onClose={() => setOpenSettingsDialog(false)}
        farmId={selectedFarm?.id || ''}
        settings={{
          name: selectedFarm?.name || '',
          description: '',
          location: selectedFarm?.location || '',
          notifications: {
            taskUpdates: true,
            workerActivity: true,
            reports: true,
          },
          privacy: {
            isPublic: false,
            showWorkers: true,
            showStats: true,
          },
          storage: {
            autoBackup: true,
            retentionDays: 30,
          },
        }}
        onUpdateSettings={handleUpdateSettings}
        onUploadImage={handleUploadImage}
      />

      {/* Manage Workers Dialog */}
      <ManageWorkersDialog
        open={openWorkersDialog}
        onClose={() => setOpenWorkersDialog(false)}
        farmId={selectedFarm?.id || ''}
        onChange={fetchFarms}
      />
    </Container>
  );
};

export default MyFarms; 